import { makeAutoObservable } from "mobx";
import { TableStore } from "src/components/tables/TableStore";
import {
  changeEntitiesState,
  loadAvailableStates,
} from "src/pages/EntityCardPage/apiEntityCard";
import { loadObjectStates } from "src/pages/ManagementPage/Obj2Tab/roles/rolesApi";
import { onError } from "src/common/onError";
import { checkEntitiesReqAttrs } from "src/pages/ManagementPage/Obj2Tab/RequiredAttributes/apiRequiredAttributes";
import { StateOption } from "src/pages/EntityCardPage/EntityCardStore";
import { ZAttribute } from "src/types/ZAttribute";
import { ZUid } from "src/types/zUid";
import { ZEntityRow, EntityFilters } from "../types";

export class ChangeStateBulkStore {
  constructor(
    public readonly tableStore: TableStore<ZEntityRow, EntityFilters>,
    public readonly objectId: ZUid,
    public readonly attributes: ZAttribute[],
    public readonly onLoad?: (flag: boolean) => void,
  ) {
    makeAutoObservable(this);
  }

  stateOptions: StateOption[] | null = null;

  setStateOptions(options: StateOption[] | null) {
    this.stateOptions = options;
  }

  stateNames: Record<ZUid, string> = {};

  setStateNames(names: Record<ZUid, string>) {
    this.stateNames = names;
  }

  async init() {
    try {
      this.setStateNames(
        (await loadObjectStates(this.objectId, { translate: true })).reduce(
          (acc, curr) => ({ ...acc, [curr.id]: curr.name }),
          {} as Record<ZUid, string>,
        ),
      );
    } catch (error) {
      onError(error);
    }
  }

  async onStart() {
    try {
      this.onLoad?.(true);
      const { objectId, firstState } = this;
      if (!firstState) return;
      const [availableStates, reqAttributes] = await Promise.all([
        loadAvailableStates(objectId, firstState, { translate: true }),
        checkEntitiesReqAttrs(this.selectedEntities),
      ]);
      this.setStateOptions(
        availableStates.map(({ stateId, workflowName }) => ({
          value: stateId,
          label: workflowName,
          unfilledAttrs: reqAttributes[stateId]?.attributeIds,
        })),
      );
    } catch (error) {
      onError(error);
    } finally {
      this.onLoad?.(false);
    }
  }

  async onFinish(stateId: ZUid) {
    const { tableStore } = this;
    try {
      this.onLoad?.(true);
      await changeEntitiesState(
        stateId,
        tableStore.selected.map((row) => row.id),
      );
      await tableStore.reload();
      /**
       * копирует поведение метода updateSelected из TableStore
       * за небольшим исключением - тут не производится проверка на keepSelected
       * но если не обновить выбранные тут строки, то в selected будет храниться старая инфа
       */
      const oldSelected = new Set(
        tableStore.selected.map((row) => row[tableStore.rowKey]),
      );
      tableStore.safeSelect(
        tableStore.rows.filter((row) =>
          oldSelected.has(row[tableStore.rowKey]),
        ),
      );
    } catch (error) {
      onError(error);
    } finally {
      this.onLoad?.(false);
      this.setStateOptions(null);
    }
  }

  get attrNamesMap(): Record<ZUid, string> | undefined {
    return this.attributes.reduce(
      (acc, { id, name }) => ({ ...acc, [id]: name }),
      {} as Record<ZUid, string>,
    );
  }

  get selectedEntities(): ZUid[] {
    return this.tableStore.selected.map(({ id }) => id);
  }

  get firstState(): ZUid | null | undefined {
    return this.tableStore.selected[0]?.stateId;
  }

  get curStateName(): string | null | undefined {
    const { stateNames, firstState } = this;
    return stateNames[firstState || ""];
  }

  get wrong(): boolean {
    const { tableStore, firstState } = this;
    return (
      !!tableStore.selected.find(({ stateId }) => stateId !== firstState) ||
      firstState === undefined
    );
  }

  get hasNextSteps(): boolean {
    return !!this.stateOptions?.length;
  }
}
